// Lib
import shallowEqual from 'shallowequal';

// Utils
import { getZoomScaleInList } from '../../../canvas/zoom/list/useScaledList';

// Constants
import {
    DROP_TARGET_BEHAVIOUR,
    DROP_TARGET_TYPES,
    ELEMENT_DND_TYPE,
} from '../../../../common/elements/elementConstants';

const getHoveredTargetIds = (monitor) => {
    if (!monitor.isDragging()) return [];
    return monitor.getTargetIds().filter((id) => monitor.isOverTarget(id) && monitor.canDropOnTarget(id));
};

const createMemoisedGetHoveredTargetProps = () => {
    let lastHoveredTypes = [];
    let lastZoomScale;
    let lastMounted = false;

    const getHoverBehaviour = (monitor, props, hoveredTypes) => {
        // There is a brief moment at the start of dragging any element, where there are no hovered target even
        // though user is hovering over a valid drop target. In order to prevent a glitch at the start of drag,
        // set the size of the element to be based on the zoom scale on the drag source.
        //
        // NOTE: I noticed that the hoveredTarget will be correctly set 1 render after the component is mounted, this
        //       is why we need to validate on prevMounted as well.
        if (props.mounted === false || lastMounted === false) {
            const item = monitor.getItem();
            return {
                zoomScaleOnHoveredTarget: item.zoomScaleOnDragSource,
                hoveredTypes: [],
            };
        }

        if (hoveredTypes.length === 0) {
            // we are over some non-interactable section of the UI (eg the header),
            // retain whatever the previous zoom was.
            return {
                zoomScaleOnHoveredTarget: lastZoomScale,
                hoveredTypes: [DROP_TARGET_BEHAVIOUR.UNINTERACTABLE],
            };
        }

        // If we're over a UI drop target (breadcrumbs, trash, inbox, etc) show at 1x scale
        // but anywhere over the canvas should respect the current zoom level.
        return {
            zoomScaleOnHoveredTarget: hoveredTypes.includes(DROP_TARGET_TYPES.CANVAS)
                ? props.zoomScale
                : getZoomScaleInList(props.platformDetails, props.gridSize),
            hoveredTypes,
        };
    };

    return (monitor, props) => {
        const hoveredTargetIds = getHoveredTargetIds(monitor);

        // Produces a map of CSS class names depending on the type of element that's hovered.
        const { hoveredTypesRegistry } = monitor.getItem();
        const currentlyHoveredTypes = hoveredTargetIds.map((id) => hoveredTypesRegistry[id]).filter(Boolean);

        const hoveredTypesHasChanged = !shallowEqual(lastHoveredTypes, currentlyHoveredTypes);
        if (hoveredTypesHasChanged) {
            lastHoveredTypes = currentlyHoveredTypes;
        }

        const { hoveredTypes, zoomScaleOnHoveredTarget } = getHoverBehaviour(monitor, props, lastHoveredTypes);

        lastZoomScale = zoomScaleOnHoveredTarget;
        lastMounted = props.mounted;

        return {
            hoveredTypes,
            zoomScaleOnHoveredTarget,
        };
    };
};

/**
 * Along with the usual hovered item information & position, this also determines what target types, if any, the item
 * is hovering over and determines the appropriate classes to add.
 */
const createCollect = () => {
    const memoisedTargetProps = createMemoisedGetHoveredTargetProps();
    let prevCollectProps = {};
    let rafThrottled = false;

    return (monitor, props) => {
        if (rafThrottled) return prevCollectProps;
        if (monitor.getItemType() !== ELEMENT_DND_TYPE) return prevCollectProps;
        prevCollectProps = {
            item: monitor.getItem(),
            itemType: monitor.getItemType(),
            clientOffset: monitor.getClientOffset(),
            sourceClientOffset: monitor.getSourceClientOffset(),
            initialSourceClientOffset: monitor.getInitialSourceClientOffset(),
            initialClientOffset: monitor.getInitialClientOffset(),
            movementOffset: monitor.getDifferenceFromInitialOffset(),
            ...memoisedTargetProps(monitor, props),
        };

        rafThrottled = true;
        requestAnimationFrame(() => {
            rafThrottled = false;
        });

        return prevCollectProps;
    };
};

export const collector = createCollect();
